/**
 * /watchlist (Consumer Portal — Fiops Borrower Portal redesign)
 *
 * Two-section page:
 *   1. Pinned — flat list of `PinnedListRow` (loans, applications, cases,
 *      and eventually rates the borrower is watching).
 *   2. Astor suggestions — mint-bordered panel with `AstorBriefingCard`
 *      header + 3-col grid of `WatchlistSuggestionCard` tiles.
 *
 * Spec: `/tmp/design-pass/borrower/specs/reference-section.md` § Watchlist.
 * Replaces the legacy DataTable-driven layout with custom row + suggestion
 * cards so the surface matches the redesigned reference-section visual.
 */

import AddIcon from '@mui/icons-material/Add';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  baseColors,
  EmptyState,
  GlassButton,
  GlassCard,
  iconSizes,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { AstorAvatar } from '../../components/loans/AstorAvatar';
import { PinnedListRow } from '../../components/watchlist/PinnedListRow';
import { type HomePin, type HomePinEntityType } from '../../components/watchlist/utils';
import { WatchlistAddDialog } from '../../components/watchlist/WatchlistAddDialog';
import { WatchlistSuggestionCard } from '../../components/watchlist/WatchlistSuggestionCard';
import { useHomeWatchlist, useRemoveHomePin } from '../../lib/trpc/hooks';

// Subset of pin types a consumer cares about. A pin from another portal
// (partner/contact/consumer) is filtered out of the rendered list.
const CONSUMER_VISIBLE_TYPES: ReadonlySet<HomePinEntityType> = new Set(['case', 'loan', 'application', 'rate']);

/**
 * Hardcoded suggestion list for now — three Astor-flavoured "things you
 * might want to watch" tiles.
 *
 * TODO(astor suggestions endpoint): replace with a real query, e.g.
 * `trpc.astor.watchlist.suggestions.useQuery({ consumerId })`. Each
 * suggestion needs to surface kind, title, sub, deep-link, and an "added"
 * marker so we stop re-suggesting after the user clicks +.
 */
// R5: design ships three DISTINCT Astor-flavoured watchlist suggestions
// (rate-index alert / lender counter-offer watch / comps tracker) rather
// than three copies of the same "+ Add" placeholder. Each card carries a
// portfolio-specific reason in the subtitle so the user can decide whether
// the suggestion is worth pinning without having to dig.
function useMockSuggestions() {
  const { t } = useI18n();
  return useMemo(
    () => [
      {
        id: 'mock-rate-index',
        icon: TrendingUpIcon,
        title: t('consumer.watchlist.suggestion.rateIndex.title'),
        subtitle: t('consumer.watchlist.suggestion.rateIndex.sub'),
      },
      {
        id: 'mock-lender-counter',
        icon: FolderIcon,
        title: t('consumer.watchlist.suggestion.lenderCounter.title'),
        subtitle: t('consumer.watchlist.suggestion.lenderCounter.sub'),
      },
      {
        id: 'mock-comps-tracker',
        icon: TrendingUpIcon,
        title: t('consumer.watchlist.suggestion.compsTracker.title'),
        subtitle: t('consumer.watchlist.suggestion.compsTracker.sub'),
      },
    ],
    [t],
  );
}

export default function WatchlistPage(): ReactElement {
  const { t } = useI18n();
  const watchlistQuery = useHomeWatchlist();
  const removePin = useRemoveHomePin();
  const [pickerOpen, setPickerOpen] = useState(false);
  const suggestions = useMockSuggestions();

  const handleRemove = useCallback(
    (pinId: string) => {
      removePin.mutate({ id: pinId });
    },
    [removePin],
  );

  // tRPC serialises Date → string on the wire, but the schema type says Date.
  // PinnedListRow tolerates both shapes for `createdAt`.
  const pins = useMemo<HomePin[]>(
    () =>
      ((watchlistQuery.data?.pins ?? []) as unknown as HomePin[]).filter((p) =>
        CONSUMER_VISIBLE_TYPES.has(p.entityType),
      ),
    [watchlistQuery.data?.pins],
  );

  return (
    <PageTemplate
      variant="list"
      titleKey="consumer.watchlist.pageTitle"
      descriptionKey="consumer.watchlist.pageSubtitle"
      icon={BookmarkIcon}
      actions={
        <GlassButton
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: iconSizes.sm }} />}
          onClick={() => setPickerOpen(true)}
        >
          {t('consumer.watchlist.addToWatchlist')}
        </GlassButton>
      }
    >
      {/* Section 1 — Pinned */}
      <GlassCard sx={{ p: 0, mb: 2.5 }}>
        <Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
            {t('consumer.watchlist.sectionPinned')}
          </Typography>
        </Box>
        {pins.length === 0 ? (
          <EmptyState
            icon={<BookmarkIcon />}
            title={t('home.watchlist.consumer.empty')}
            description={t('home.watchlist.consumer.emptyDescription')}
          />
        ) : (
          <Stack divider={<Box sx={{ height: 1, bgcolor: 'divider', mx: 1.5 }} />}>
            {pins.map((pin) => (
              <PinnedListRow key={pin.id} pin={pin} onRemove={handleRemove} />
            ))}
          </Stack>
        )}
      </GlassCard>

      {/* Section 2 — Astor suggestions */}
      <Box
        sx={{
          p: 2.25,
          borderRadius: 2,
          border: `1px solid ${baseColors.teal[200]}`,
          bgcolor: 'background.paper',
          background: `linear-gradient(135deg, ${baseColors.teal[50]} 0%, transparent 70%)`,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', mb: 2 }}>
          <AstorAvatar size={28} pulse />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
              {t('consumer.watchlist.astorSuggestionsTitle')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {t('consumer.watchlist.astorSuggestionsSubtitle')}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {suggestions.map((s) => (
            <WatchlistSuggestionCard
              key={s.id}
              icon={s.icon}
              title={s.title}
              subtitle={s.subtitle}
              onAdd={() => setPickerOpen(true)}
            />
          ))}
        </Box>
      </Box>

      <WatchlistAddDialog open={pickerOpen} onClose={() => setPickerOpen(false)} />
      {/* R10: dropped the trailing "Back to home" button — the page header
          already carries the breadcrumb back to /dashboard, the extra button
          was redundant chrome the design doesn't include. */}
    </PageTemplate>
  );
}
